import type { InvoiceStatus, PublicInvoice } from "./types";

/**
 * Live invoice state for the checkout. Three sources feed it: the initial GET, SSE events and the
 * polling fallback. They can arrive out of order, so the reducer never lets a stale message move an
 * invoice backwards (a late "pending" must not undo "confirming"; nothing undoes a final state
 * except a full, authoritative invoice from the server).
 */

export const FINAL_STATUSES: readonly InvoiceStatus[] = ["paid", "underpaid", "overpaid", "expired", "flagged"];

export function isFinal(status: InvoiceStatus): boolean {
  return FINAL_STATUSES.includes(status);
}

/** Money arrived and the order is settled from the customer's point of view. */
export function isSettled(status: InvoiceStatus): boolean {
  return status === "paid" || status === "overpaid";
}

function rank(status: InvoiceStatus): number {
  if (status === "pending") return 0;
  if (status === "partially_paid") return 1;
  if (status === "confirming") return 2;
  return 3;
}

export type Transport = "connecting" | "live" | "polling";

export interface InvoiceState {
  invoice: PublicInvoice | null;
  loading: boolean;
  /** Error code of the last failed load while nothing is shown yet ("not_found", "network", …). */
  error: string | null;
  transport: Transport;
  /** Epoch ms of the last message from the server. */
  updatedAt: number | null;
}

export const initialInvoiceState: InvoiceState = {
  invoice: null,
  loading: true,
  error: null,
  transport: "connecting",
  updatedAt: null,
};

export type InvoiceAction =
  /** Full invoice from GET (initial load or poll). Authoritative. */
  | { type: "loaded"; invoice: PublicInvoice; at: number }
  /** Partial update from an SSE message. Applied only if it moves the invoice forward. */
  | { type: "event"; patch: Partial<PublicInvoice>; at: number }
  | { type: "load_failed"; error: string }
  | { type: "transport"; transport: Transport };

export function mergeInvoice(current: PublicInvoice, patch: Partial<PublicInvoice>): PublicInvoice {
  if (patch.id && patch.id !== current.id) return current;
  const next = patch.status;
  if (next && next !== current.status) {
    const from = rank(current.status);
    const to = rank(next);
    // Backwards, or sideways between two final states, is left to the authoritative GET.
    if (to < from || (to === 3 && from === 3)) {
      return { ...current, ...stripStatusFields(patch) };
    }
  }
  const merged: PublicInvoice = { ...current };
  for (const [key, value] of Object.entries(patch) as [keyof PublicInvoice, unknown][]) {
    // An event that omits or nulls a field we already know does not erase it.
    if (value === undefined || (value === null && merged[key] !== null)) continue;
    (merged as Record<string, unknown>)[key] = value;
  }
  return merged;
}

function stripStatusFields(patch: Partial<PublicInvoice>): Partial<PublicInvoice> {
  // Keep only facts that never regress (e.g. a tx hash learned from the stream).
  const safe: Partial<PublicInvoice> = {};
  if (patch.txHash) safe.txHash = patch.txHash;
  if (patch.explorerUrl) safe.explorerUrl = patch.explorerUrl;
  return safe;
}

export function invoiceReducer(state: InvoiceState, action: InvoiceAction): InvoiceState {
  switch (action.type) {
    case "loaded": {
      const current = state.invoice;
      let invoice = action.invoice;
      // A poll that raced an SSE event may still say "pending" after we saw "confirming".
      if (current && current.id === invoice.id && !isFinal(invoice.status) && rank(invoice.status) < rank(current.status)) {
        invoice = { ...invoice, status: current.status, txHash: invoice.txHash ?? current.txHash };
      }
      return { ...state, invoice, loading: false, error: null, updatedAt: action.at };
    }
    case "event": {
      if (!state.invoice) return state;
      return { ...state, invoice: mergeInvoice(state.invoice, action.patch), updatedAt: action.at };
    }
    case "load_failed":
      return state.invoice ? state : { ...state, loading: false, error: action.error };
    case "transport":
      return state.transport === action.transport ? state : { ...state, transport: action.transport };
  }
}

/** What the checkout shows. A pending invoice whose clock ran out is shown as expired locally. */
export type CheckoutView = "pay" | "confirming" | InvoiceStatus;

export function checkoutView(invoice: PublicInvoice, nowMs: number): CheckoutView {
  if (invoice.status === "pending") {
    return Date.parse(invoice.expiresAt) <= nowMs ? "expired" : "pay";
  }
  // Part of the amount arrived: still payable until expiry (the rest only); after it, wait for the final word.
  if (invoice.status === "partially_paid") {
    return Date.parse(invoice.expiresAt) <= nowMs ? "confirming" : "pay";
  }
  return invoice.status;
}
